import * as React from 'react';

export const INLINE_PHRASING_TAG_LIST = [
  'span',
  'strong',
  'em',
  'b',
  'i',
  'small',
  'sub',
  'sup',
  'ins',
  'del',
  'mark',
] as const;

export type InlinePhrasingTag = (typeof INLINE_PHRASING_TAG_LIST)[number];

export const INLINE_PHRASING_TAGS = new Set<string>(INLINE_PHRASING_TAG_LIST);

export function formatInlinePhrasingTagsCopy(
  conjunction: 'or' | 'and' = 'or',
): string {
  const quoted = INLINE_PHRASING_TAG_LIST.map((tag) => `\`${tag}\``);
  if (quoted.length <= 1) {
    return quoted[0] ?? '';
  }
  return `${quoted.slice(0, -1).join(', ')}, ${conjunction} ${quoted[quoted.length - 1]}`;
}

const UNSAFE_URL_CHARACTER = new RegExp('[\\p{Cc}\\p{Cf}\\p{Z}]', 'u');
const EXPLICIT_URL_SCHEME = /^([a-z][a-z0-9+.-]*):/i;
const SAFE_INLINE_LINK_SCHEMES = new Set(['http', 'https', 'mailto', 'tel']);

export const InlineCopyContext = React.createContext(false);

/**
 * True inside a phrasing host such as a radio label. Layout and text tags
 * stay phrasing content, and authored type size, color, and weight are kept.
 */
export const PhrasingHostContext = React.createContext(false);

export function PhrasingHost({
  children,
}: {
  children?: React.ReactNode;
}): React.ReactElement {
  return (
    <PhrasingHostContext.Provider value={true}>
      {children}
    </PhrasingHostContext.Provider>
  );
}

/** Host class so nested phrasing/links inherit type size via CSS. */
export const INLINE_COPY_HOST_CLASS = 'inline-copy-host';

export function InlineCopyBoundary({
  children,
}: {
  children?: React.ReactNode;
}): React.ReactElement {
  return (
    <InlineCopyContext.Provider value={true}>
      {children}
    </InlineCopyContext.Provider>
  );
}

/**
 * Nested Link / phrasing Text must match the host type size. Puck merges
 * catalog defaults (`variant: 'b4'`, `size: 'md'`) onto every node, so an
 * unset check is not enough — drop the scale whenever we are nested.
 */
export function inheritHostTypeScale<T>(
  nested: boolean,
  value: T | undefined,
): T | null | undefined {
  return nested ? null : value;
}

export function resolveNestedInlineTextTag(
  tag: string | undefined,
): InlinePhrasingTag {
  const resolved = (tag ?? 'p').toLowerCase();
  return INLINE_PHRASING_TAGS.has(resolved)
    ? (resolved as InlinePhrasingTag)
    : 'span';
}

export function isSafeInlineLinkHref(href: string): boolean {
  if (UNSAFE_URL_CHARACTER.test(href)) return false;
  const scheme = EXPLICIT_URL_SCHEME.exec(href)?.[1]?.toLowerCase();
  return scheme ? SAFE_INLINE_LINK_SCHEMES.has(scheme) : true;
}
